import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Loader2, Plus, FileDown, Copy, CheckCircle2, Clock, Truck, X } from "lucide-react";

function CreateModal({ onClose, onCreated }) {
    const [customers, setCustomers] = useState([]);
    const [form, setForm] = useState({ customer_id: "", destination_country: "", transport_mode: "Abholung durch Käufer", arrival_place: "", arrival_date: "" });
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        (async () => {
            try {
                const { data } = await api.get("/customers?limit=500");
                const list = Array.isArray(data) ? data : (data.items || []);
                // Dublettenbereinigung: pro Kunden-ID nur einen Eintrag
                const seen = new Set();
                setCustomers(list.filter((c) => (c.id && !seen.has(c.id) && seen.add(c.id))));
            } catch { /* ignore */ }
        })();
    }, []);

    const submit = async () => {
        if (!form.customer_id) { toast.error("Bitte einen Kunden wählen."); return; }
        setBusy(true);
        try {
            const { data } = await api.post("/gelangensnachweis", form);
            toast.success(data?.email_sent
                ? "Gelangensnachweis erstellt — Bestätigungs-Link per E-Mail an den Käufer gesendet."
                : "Gelangensnachweis erstellt — Link kann an den Käufer gesendet werden.");
            onCreated();
        } catch (e) { toast.error(e.response?.data?.detail || "Fehler beim Erstellen."); }
        setBusy(false);
    };

    return (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-start justify-center p-4 overflow-y-auto" data-testid="gn-create-modal" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
            <div className="w-full max-w-lg mt-12 bg-white border border-gray-200 rounded-sm shadow-xl">
                <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200 bg-[#111827] text-white rounded-t-sm">
                    <h2 className="font-display text-lg font-black">Neuer Gelangensnachweis</h2>
                    <button type="button" onClick={onClose} className="p-1 rounded-sm hover:bg-white/10" data-testid="gn-create-close"><X className="w-4 h-4" /></button>
                </div>
                <div className="p-5 space-y-3">
                    <div>
                        <label className="text-[11px] font-bold uppercase tracking-wider text-gray-500">Kunde (Abnehmer) *</label>
                        <select value={form.customer_id} onChange={(e) => setForm({ ...form, customer_id: e.target.value })}
                            className="w-full h-10 text-sm rounded-sm border border-gray-300 px-2 mt-1 focus:border-[#111827] focus:outline-none" data-testid="gn-customer-select">
                            <option value="">— Kunde wählen —</option>
                            {customers.map((c) => (
                                <option key={c.id} value={c.id}>
                                    {(c.company_name || `${c.first_name || ""} ${c.last_name || ""}`).trim()}{c.ust_id ? ` · ${c.ust_id}` : ""}{c.country ? ` (${c.country})` : ""}
                                </option>
                            ))}
                        </select>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                        <div>
                            <label className="text-[11px] font-bold uppercase tracking-wider text-gray-500">Bestimmungsland</label>
                            <Input value={form.destination_country} onChange={(e) => setForm({ ...form, destination_country: e.target.value })} placeholder="z. B. Österreich" className="rounded-sm mt-1" data-testid="gn-dest-input" />
                        </div>
                        <div>
                            <label className="text-[11px] font-bold uppercase tracking-wider text-gray-500">Beförderung</label>
                            <Input value={form.transport_mode} onChange={(e) => setForm({ ...form, transport_mode: e.target.value })} className="rounded-sm mt-1" data-testid="gn-transport-input" />
                        </div>
                    </div>
                    <p className="text-[11px] text-gray-400">Der Käufer bestätigt den Erhalt online per Link (ohne Unterschrift, § 17a Abs. 2 Nr. 2 UStDV) — alternativ kann das PDF zum Gegenzeichnen gedruckt werden.</p>
                </div>
                <div className="flex items-center justify-end gap-2 px-5 py-4 border-t border-gray-200 bg-gray-50 rounded-b-sm">
                    <Button type="button" variant="outline" onClick={onClose} className="h-9 rounded-sm text-sm">Abbrechen</Button>
                    <Button type="button" onClick={submit} disabled={busy} className="h-9 rounded-sm bg-[#111827] text-[#FFCC00] hover:bg-black text-sm font-bold" data-testid="gn-create-submit">
                        {busy ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <Plus className="w-4 h-4 mr-1.5" />} Erstellen
                    </Button>
                </div>
            </div>
        </div>
    );
}

function ZmSection() {
    const prevMonth = () => {
        const d = new Date(); d.setDate(1); d.setMonth(d.getMonth() - 1);
        return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
    };
    const [month, setMonth] = useState(prevMonth());
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        (async () => {
            setLoading(true);
            try {
                const r = await api.get(`/tax/zm-report?month=${month}`);
                setData(r.data);
            } catch { setData(null); }
            setLoading(false);
        })();
    }, [month]);

    const exportCsv = async () => {
        try {
            const r = await api.get(`/tax/zm-report.csv?month=${month}`, { responseType: "blob" });
            const url = URL.createObjectURL(new Blob([r.data], { type: "text/csv" }));
            const a = document.createElement("a");
            a.href = url; a.download = `ZM_${month}.csv`; a.click(); URL.revokeObjectURL(url);
        } catch { toast.error("CSV-Export fehlgeschlagen."); }
    };

    const fmtEur = (v) => `${(v || 0).toLocaleString("de-DE", { minimumFractionDigits: 2 })} €`;
    return (
        <div className="bg-white border border-gray-200 rounded-sm p-4 mb-8" data-testid="zm-section">
            <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
                <div>
                    <h2 className="font-display text-lg font-black text-[#111827]">Zusammenfassende Meldung (ZM)</h2>
                    <p className="text-xs text-gray-500">Innergemeinschaftliche Lieferungen je Monat — elektronisch bis zum 25. des Folgemonats über das BZSt-Online-Portal melden (§ 18a UStG).</p>
                </div>
                <div className="flex items-center gap-2">
                    <input type="month" value={month} onChange={(e) => setMonth(e.target.value)}
                        className="h-9 border border-gray-300 rounded-sm px-2 text-sm focus:outline-none focus:border-[#111827]" data-testid="zm-month-input" />
                    <Button type="button" variant="outline" size="sm" onClick={exportCsv} disabled={!data || !(data.items || []).length} className="h-9 rounded-sm text-xs" data-testid="zm-csv-btn">
                        <FileDown className="w-3.5 h-3.5 mr-1" /> CSV (BZSt)
                    </Button>
                </div>
            </div>
            {loading ? (
                <div className="flex items-center gap-2 text-gray-500 text-sm py-4"><Loader2 className="w-4 h-4 animate-spin" /> Lädt…</div>
            ) : !data || (data.items || []).length === 0 ? (
                <div className="text-sm text-gray-400 py-4" data-testid="zm-empty">Keine innergemeinschaftlichen Lieferungen im Monat {month}.</div>
            ) : (
                <>
                    {data.deadline && (
                        <div className="text-[11px] font-bold text-amber-900 bg-amber-50 border-l-4 border-amber-500 px-3 py-2 rounded-sm mb-2" data-testid="zm-deadline-hint">
                            Meldefrist: {data.deadline.split("-").reverse().join(".")} — verspätete oder fehlerhafte ZM-Meldungen gefährden die Steuerfreiheit!
                        </div>
                    )}
                    {data.missing_ust_id > 0 && (
                        <div className="text-[11px] font-bold text-red-800 bg-red-50 border-l-4 border-red-500 px-3 py-2 rounded-sm mb-2" data-testid="zm-missing-vat-hint">
                            ⚠ {data.missing_ust_id} Abnehmer ohne USt-IdNr. — bitte im Kundenstamm ergänzen und via VIES prüfen.
                        </div>
                    )}
                    <table className="w-full text-sm" data-testid="zm-table">
                        <thead>
                            <tr className="text-left text-[10px] uppercase tracking-wider text-gray-400 border-b border-gray-200">
                                <th className="py-1.5">Land</th><th>USt-IdNr.</th><th>Abnehmer</th><th className="text-right">Rechnungen</th><th className="text-right">Summe (netto)</th><th className="text-right">VIES</th>
                            </tr>
                        </thead>
                        <tbody>
                            {data.items.map((g, i) => (
                                <tr key={i} className="border-b border-gray-100" data-testid={`zm-row-${i}`}>
                                    <td className="py-1.5 font-bold">{g.country}</td>
                                    <td className="font-mono-num">{g.ust_id || <span className="text-red-600 font-bold">fehlt!</span>}</td>
                                    <td>{g.customer_name || "—"}</td>
                                    <td className="text-right">{g.invoice_count}</td>
                                    <td className="text-right font-bold">{fmtEur(g.total_net)}</td>
                                    <td className="text-right">{g.vies_checked ? <span className="text-emerald-600 font-bold">✓</span> : <span className="text-amber-600">offen</span>}</td>
                                </tr>
                            ))}
                        </tbody>
                        <tfoot>
                            <tr>
                                <td colSpan={4} className="py-2 font-bold text-right">Gesamt</td>
                                <td className="text-right font-black" data-testid="zm-total">{fmtEur(data.total_net)}</td><td />
                            </tr>
                        </tfoot>
                    </table>
                </>
            )}
        </div>
    );
}

export default function GelangensnachweisPage() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showCreate, setShowCreate] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/gelangensnachweis");
            setItems(data.items || []);
        } catch { toast.error("Konnte Gelangensnachweise nicht laden."); }
        setLoading(false);
    }, []);

    useEffect(() => { load(); }, [load]);

    const copyLink = (url) => {
        navigator.clipboard?.writeText(url);
        toast.success("Bestätigungs-Link kopiert.");
    };

    const downloadPdf = async (gn) => {
        try {
            const r = await api.get(`/gelangensnachweis/${gn.id}/pdf`, { responseType: "blob" });
            const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
            const a = document.createElement("a");
            a.href = url; a.download = `Gelangensbestaetigung_${gn.invoice_number || gn.id.slice(0, 8)}.pdf`; a.click();
            URL.revokeObjectURL(url);
        } catch { toast.error("PDF-Download fehlgeschlagen."); }
    };

    const buyerName = (gn) => {
        const b = gn.buyer || {};
        return b.company_name || [b.first_name, b.last_name].filter(Boolean).join(" ") || "—";
    };
    const vehName = (gn) => {
        const v = gn.vehicle || {};
        return v.title || [v.brand, v.model].filter(Boolean).join(" ") || "—";
    };

    return (
        <div className="max-w-5xl mx-auto px-4 py-8" data-testid="gelangensnachweis-page">
            <div className="flex items-center justify-between mb-1">
                <h1 className="font-display text-3xl font-black text-[#111827]">Gelangensnachweise</h1>
                <Button type="button" onClick={() => setShowCreate(true)} className="h-10 rounded-sm bg-[#111827] text-[#FFCC00] hover:bg-black font-bold" data-testid="gn-new-btn">
                    <Plus className="w-4 h-4 mr-1.5" /> Neuer Nachweis
                </Button>
            </div>
            <p className="text-sm text-gray-500 mb-6">Belegnachweis für steuerfreie innergemeinschaftliche Lieferungen (§ 6a UStG / § 17a UStDV).</p>

            <ZmSection />

            {loading ? (
                <div className="flex items-center gap-2 text-gray-500"><Loader2 className="w-5 h-5 animate-spin" /> Lädt…</div>
            ) : items.length === 0 ? (
                <div className="text-center text-gray-400 py-16 border border-dashed border-gray-300 rounded-sm" data-testid="gn-empty">
                    <Truck className="w-10 h-10 mx-auto mb-2 opacity-40" />
                    Noch keine Gelangensnachweise. Erstellen Sie einen für eine EU-Lieferung.
                </div>
            ) : (
                <div className="space-y-2" data-testid="gn-list">
                    {items.map((gn) => (
                        <div key={gn.id} className="bg-white border border-gray-200 rounded-sm p-4 flex flex-wrap items-center gap-3" data-testid={`gn-row-${gn.id}`}>
                            <div className="flex-1 min-w-[200px]">
                                <div className="font-bold text-[#111827]">{vehName(gn)}</div>
                                <div className="text-xs text-gray-500">{buyerName(gn)} · {gn.destination_country || "—"}{gn.invoice_number ? ` · ${gn.invoice_number}` : ""}</div>
                                {gn.vies && (
                                    gn.vies.valid === true ? (
                                        <span className="inline-block mt-1 text-[10px] font-bold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-sm px-1.5 py-0.5" data-testid="gn-vies-valid">USt-IdNr. VIES-geprüft ✓</span>
                                    ) : gn.vies.valid === false ? (
                                        <span className="inline-block mt-1 text-[10px] font-bold text-red-700 bg-red-50 border border-red-200 rounded-sm px-1.5 py-0.5" data-testid="gn-vies-invalid">USt-IdNr. VIES-ungültig</span>
                                    ) : (
                                        <span className="inline-block mt-1 text-[10px] font-bold text-gray-500 bg-gray-50 border border-gray-200 rounded-sm px-1.5 py-0.5" data-testid="gn-vies-unknown">VIES nicht prüfbar</span>
                                    )
                                )}
                            </div>
                            {gn.status === "confirmed" ? (
                                <span className="inline-flex items-center gap-1 text-xs font-bold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-sm px-2 py-1" data-testid="gn-status-confirmed">
                                    <CheckCircle2 className="w-3.5 h-3.5" /> Bestätigt {gn.arrival_date ? `· ${gn.arrival_date}` : ""}
                                </span>
                            ) : (
                                <span className="inline-flex items-center gap-1 text-xs font-bold text-amber-700 bg-amber-50 border border-amber-200 rounded-sm px-2 py-1" data-testid="gn-status-pending">
                                    <Clock className="w-3.5 h-3.5" /> Offen
                                </span>
                            )}
                            {gn.status !== "confirmed" && (
                                <Button type="button" variant="outline" size="sm" onClick={() => copyLink(gn.confirm_url)} className="h-8 rounded-sm text-xs" data-testid="gn-copy-link">
                                    <Copy className="w-3.5 h-3.5 mr-1" /> Link
                                </Button>
                            )}
                            <Button type="button" variant="outline" size="sm" onClick={() => downloadPdf(gn)} className="h-8 rounded-sm text-xs" data-testid="gn-pdf-btn">
                                <FileDown className="w-3.5 h-3.5 mr-1" /> PDF
                            </Button>
                        </div>
                    ))}
                </div>
            )}

            {showCreate && <CreateModal onClose={() => setShowCreate(false)} onCreated={() => { setShowCreate(false); load(); }} />}
        </div>
    );
}
